/**
 * AtlasWorldMapModule — the World Atlas (N): a pan/zoom map of the Commons
 * with every settlement named, civ-colored, and clickable into a FULL stats
 * panel — governance, laws, people, economy, wars, and the versonas who live
 * there — plus a Civilizations tab aggregating both nations.
 *
 * Data doctrine: display only, the server is the authority on every row.
 * Cheap 312-row tables subscribe when the window first opens; per-settlement
 * detail (agents, stock, laws, graves, chronicle…) subscribes lazily WHERE
 * settlement_id = <sid> on selection (the AtlasChronicleModule pattern), and
 * the Civilizations tab pulls the two global tables it aggregates
 * (npc_agent, grave) only when first shown.
 *
 * Names: `settlement_name` rows are sparse admin renames — everything else
 * derives deterministically from the seed (lib/settlement_names.ts), so every
 * client reads the identical atlas. The terrain underlay is sampled from the
 * REAL height/biome fields (shared/terrain) in async slices per view.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { BATTLE } from '@shared/living_events.js';
import { terrainHeight, biomeAt } from '../../../shared/terrain.js';
import { forestDensity } from '../lib/forest_field.js';
import { civilizationOf, type Civilization } from '../lib/biome_settlements.js';
import { civOfSettlementRowC, civRowGeneration } from '../lib/civ_row.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { peekCircuit } from '../lib/race_circuit.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { sampleWxRow, tempColor, humidityColor, WEATHER_CLASS_COLOR, type WeatherClass } from '../lib/weather.js';
import { currentTod } from './SkyModule.js';
import { myWaypoints, promptCreateWaypoint, promptEditWaypoint, drawWaypointMarker } from '../lib/waypoints.js';
import { sampleConflictRow } from '../lib/conflict_map.js';
import { officeLabel } from '../lib/offices_wave.js';
import { isServiceKey } from '../lib/service_posts.js';
import { TIER_VOTES } from '../lib/federal_core.js';

const rowFirstCiv = (siteSeed: number, siteId?: number): Civilization =>
  siteId === undefined ? civilizationOf(siteSeed) : civOfSettlementRowC(BigInt(siteId + 1), siteSeed);

// ── Row shapes (display-only mirrors of the server schema) ────────────────────
interface Ts { microsSinceUnixEpoch: bigint }
interface NameRow { siteId: bigint; name: string }
interface CivRow { settlementId: bigint; civ: string }
interface EconRow {
  settlementId: bigint; centerX: number; centerZ: number;
  stockFood: number; stockOre: number; stockTools: number; stockLuxury: number;
  priceFood: number; priceOre: number; priceTools: number; priceLuxury: number;
  treasury: bigint; taxBps: number; mayorId: bigint; nextElectionAt: Ts;
  projectKey: string; projectProgress: number; projectTarget: number;
}
interface OfficeRow { settlementId: bigint; office: string; holderId: bigint; holderName: string; sinceAt: Ts }
interface WarRow { aggressorSid: bigint; defenderSid: bigint; status: string; grievanceAtStart: number; exhaustionA: number; exhaustionB: number; casualties: number; startedAt: Ts }
interface RivalryRow { aId: bigint; bId: bigint; grievance: number }
// SOCIETY WAVE (2026-09-19): the land, the town's scores and its seat in the Assembly.
interface NodeRow { settlementId: bigint; good: string; x: number; z: number; richness: number }
interface ScoreRow { settlementId: bigint; cohesion: number; readiness: number; learning: number; health: number; order: number; piety: number; prosperity: number }
interface MemberRow { settlementId: bigint; civ: string; tier: string; votes: number; suspended: boolean; defiant: boolean; arrears: bigint }
interface LinkRow { settlementId: bigint; citySid: bigint }
interface SpecRow { settlementId: bigint; spec: string }
interface SlotRow { settlementId: bigint; recipeKey: string; holderId: bigint }
interface AgentRow { versonaId: bigint; settlementId: bigint; job: string; wallet: bigint; hunger: number; mood: number; needRest: number }
interface VersonaRow { versonaId: bigint; homeCityId: bigint; curX: number; curZ: number; state: string }
interface HouseholdRow { householdId: bigint; settlementId: bigint; surname: string; headId: bigint }
interface HouseholdMemberRow { householdId: bigint; versonaId: bigint }
interface FaithRow { versonaId: bigint; faithId: string; intensity: number }
interface GraveRow { settlementId: bigint; name: string; cause: string; diedAt: Ts }
interface JobSlotRow { settlementId: bigint; fn: string; recipeKey: string; wage: number; holderId: bigint }
interface StockRow { settlementId: bigint; commodity: string; qty: number }
interface RoundRow { settlementId: bigint; commodity: string; avgPrice: number; volume: number; unfilled: number; epoch: number }
interface ClauseRow { settlementId: bigint; kind: string; value: number; status: string; rationale: string; yesVotes: number }
interface NormRow { settlementId: bigint; key: string; text: string; adoptionX10: number; active: boolean }
interface ResidencyRow { settlementId: bigint; paidUntil: Ts }
interface ExchangeRow { settlementId: bigint }

const CIV_COLOR: Record<Civilization, string> = { a: '#d9a94e', b: '#7fc4e8' };
const CIV_TITLE: Record<Civilization, string> = { a: 'Aurelian Concord', b: 'Borean Compact' };
const CIV_FOLK: Record<Civilization, string> = { a: 'Aurelians', b: 'Boreans' };
const TIER_R: Record<string, number> = { city: 7, town: 4.5, hamlet: 2.8, capital: 12, district: 0, circuit: 6 }; // district 0 = drawn as its capital
const BIOME_COLOR: Record<string, [number, number, number]> = {
  regolith_plains: [96, 88, 74], crystal_forest: [88, 74, 116], dune_desert: [128, 108, 70],
  rocky_highlands: [84, 82, 86], moss_wetland: [58, 88, 62], fungal_basin: [60, 92, 92],
};
const OFFICE_LABEL: Record<string, string> = { keeper: 'Keeper of the Granary', guildmaster: 'Guildmaster' };

/** DB id space: settlement rows key settlementId = site.id + 1 (0 = none/wilds). */
const dbSid = (siteId: number): number => siteId + 1;

const esc = (v: string): string => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const coin = (v: bigint | number): string => `ⓒ${Number(v).toLocaleString()}`;
const pretty = (k: string): string => k.replace(/_/g, ' ');
const inH = (t: Ts): string => {
  const ms = Number(t.microsSinceUnixEpoch / 1000n) - Date.now();
  if (ms <= 0) return 'now';
  return ms < 3_600_000 ? `in ${Math.round(ms / 60000)}m` : `in ${Math.round(ms / 3_600_000)}h`;
};
const ago = (t: Ts): string => {
  const s = Math.max(0, (Date.now() - Number(t.microsSinceUnixEpoch / 1000n)) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return s < 86400 ? `${Math.round(s / 3600)}h ago` : `${Math.round(s / 86400)}d ago`;
};

export class AtlasWorldMapModule implements IModule {
  readonly name = 'AtlasWorldMap';

  private win: UiWindow | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private wrap: HTMLDivElement | null = null;
  private tip: HTMLDivElement | null = null;
  private panel: HTMLDivElement | null = null;
  private search = '';
  private tab: 'settlement' | 'civ' = 'settlement';
  /** SWG shuttle mode: clicking a city/town books the hop instead of only
   *  selecting it (opened from a shuttle pad via AtlasTravelModule). */
  private travelMode = false;
  private travelBanner: HTMLDivElement | null = null;

  private seed = '';
  private civGen = -1;
  private sites: SettlementSite[] = [];
  private derivedNames = new Map<number, string>();
  private selected: SettlementSite | null = null;
  private hovered: SettlementSite | null = null;
  private tipFor: SettlementSite | null = null;

  private view = { cx: 0, cz: 0, scale: 0.001 }; // px per meter
  private dirty = true;
  private raf = 0;
  private timer: number | null = null;
  private openSubbed = false;
  private globalJoinsSubbed = false;
  private civTabSubbed = false;
  private readonly sidSubbed = new Set<string>();

  // Terrain underlay: async-sampled image of the current (quantized) view.
  private under: { img: HTMLCanvasElement; x0: number; z0: number; x1: number; z1: number } | null = null;
  private underKey = '';
  private underBuilding = '';
  private sea = 0;

  // Weather overlay (toggleable): async-sampled meteo field over the view.
  private wxMode: 'none' | 'temp' | 'weather' | 'humidity' | 'civ' | 'war' = 'none';
  /** true while a live battle's pulsing dot is drawn (the loop redraws every frame) */
  private pulse = false;
  private wxOver: { img: HTMLCanvasElement; x0: number; z0: number; x1: number; z1: number } | null = null;
  private wxKey = '';
  private wxBuilding = '';
  private wxBuildingSpatial = '';
  private wxButtons = new Map<string, HTMLButtonElement>();

  init(_app: App): void {
    void _app;
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyN' || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null || atlas.spaceFlight) return; // in space N cycles nav targets (key-collision audit 2026-08-24)
      this.toggle();
    });
    // Diagnostic surface (headless probes — no stable DOM ids on map buttons):
    // open the window, flip the weather layer, and read the build state.
    (window as unknown as Record<string, unknown>).__wxMap = {
      open: (): void => { if (!this.win?.isOpen()) this.toggle(); },
      set: (m: 'none' | 'temp' | 'weather' | 'humidity' | 'civ' | 'war'): void => this.setWxMode(m),
      state: (): { mode: string; built: boolean; building: boolean; winOpen: boolean; seed: boolean } =>
        ({ mode: this.wxMode, built: this.wxKey !== '', building: this.wxBuilding !== '', winOpen: !!this.win?.isOpen(), seed: this.seed !== '' }),
    };
  }

  // ── Data access ───────────────────────────────────────────────────────────
  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  private sub(sql: string[]): void {
    // One builder per query: a rejected query (missing table on an older
    // publish, unindexed column) must not sink its siblings in the batch.
    for (const q of sql) {
      try { atlas.conn?.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish server */ }
    }
  }

  private ensureWorld(): boolean {
    const conn = atlas.conn;
    if (!conn) return false;
    for (const w of this.tbl<{ kind: string; terrainSeed: string }>('world')) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed || this.civGen !== civRowGeneration()) {
        this.civGen = civRowGeneration();
        this.seed = w.terrainSeed;
        this.sites = settlementSites(this.seed);
        this.derivedNames = settlementNamesFor(this.sites, rowFirstCiv);
        this.sea = commonsSeaLevel(this.seed);
        this.fitAll();
      }
      return true;
    }
    return false;
  }

  private ensureOpenSubs(): void {
    if (this.openSubbed) return;
    this.openSubbed = true;
    this.sub([
      'SELECT * FROM settlement_name', 'SELECT * FROM settlement_civ',
      'SELECT * FROM settlement_econ', 'SELECT * FROM settlement_office',
      'SELECT * FROM war_state', 'SELECT * FROM settlement_rivalry', 'SELECT * FROM war_assignment',
      'SELECT * FROM battle', 'SELECT * FROM battle_unit', 'SELECT * FROM battle_record',
      'SELECT * FROM exchange_office', 'SELECT * FROM residency',
    ]);
  }

  private readonly sidOrder: string[] = [];
  private readonly sidHandles = new Map<string, Array<{ unsubscribe(): void }>>();
  /** sub() variant that keeps handles so a settlement's queries can release. */
  private subH(sql: string[]): Array<{ unsubscribe(): void }> {
    const out: Array<{ unsubscribe(): void }> = [];
    for (const q of sql) {
      try {
        const h = atlas.conn?.subscriptionBuilder().subscribe([q]) as { unsubscribe(): void } | undefined;
        if (h) out.push(h);
      } catch { /* pre-publish server */ }
    }
    return out;
  }
  private ensureSidSubs(sid: bigint): void {
    const k = String(sid);
    if (this.sidSubbed.has(k)) return;
    this.sidSubbed.add(k);
    // (bug sweep) LRU: clicking through N settlements stacked 9 live queries
    // each with no release path — cap at 6 towns, oldest unsubscribes.
    // (the dead `versona WHERE home_city_id` query is gone — the column is 0.)
    this.sidOrder.push(k);
    while (this.sidOrder.length > 6) {
      const old = this.sidOrder.shift()!;
      this.sidSubbed.delete(old);
      for (const h of this.sidHandles.get(old) ?? []) { try { h.unsubscribe(); } catch { /* gone */ } }
      this.sidHandles.delete(old);
    }
    this.sidHandles.set(k, this.subH([
      `SELECT * FROM npc_agent WHERE settlement_id = ${sid}`,
      `SELECT * FROM household WHERE settlement_id = ${sid}`,
      `SELECT * FROM grave WHERE settlement_id = ${sid}`,
      `SELECT * FROM job_slot WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_stock WHERE settlement_id = ${sid}`,
      `SELECT * FROM market_round WHERE settlement_id = ${sid}`,
      `SELECT * FROM charter_clause WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_norm WHERE settlement_id = ${sid}`,
      // society wave: the land (deposit nodes), the seven scores, the federal seat, the trading habit
      `SELECT * FROM resource_node WHERE settlement_id = ${sid}`,
      `SELECT * FROM settlement_score WHERE settlement_id = ${sid}`,
      `SELECT * FROM federal_member WHERE settlement_id = ${sid}`,
      `SELECT * FROM market_link WHERE settlement_id = ${sid}`,
      `SELECT * FROM city_spec WHERE settlement_id = ${sid}`,
    ]));
    if (!this.globalJoinsSubbed) {
      // Joins with no settlement column (member→household, faith per versona).
      this.globalJoinsSubbed = true;
      this.sub(['SELECT * FROM household_member', 'SELECT * FROM faith_adherence']);
    }
  }

  private ensureCivTabSubs(): void {
    if (this.civTabSubbed) return;
    this.civTabSubbed = true;
    this.sub(['SELECT * FROM npc_agent', 'SELECT * FROM grave']);
  }

  private nameOf(s: SettlementSite): string {
    const admin = this.tbl<NameRow>('settlementName').find((n) => Number(n.siteId) === s.id);
    return admin?.name ?? this.derivedNames.get(s.id) ?? `settlement #${s.id}`;
  }

  private civOf(s: SettlementSite): Civilization {
    const row = this.tbl<CivRow>('settlementCiv').find((c) => Number(c.settlementId) === dbSid(s.id));
    return (row?.civ === 'a' || row?.civ === 'b') ? row.civ : civilizationOf(s.siteSeed);
  }

  private econOf(s: SettlementSite): EconRow | undefined {
    return this.tbl<EconRow>('settlementEcon').find((e) => Number(e.settlementId) === dbSid(s.id));
  }

  private warsOf(siteId: number): WarRow[] {
    const id = dbSid(siteId);
    return this.tbl<WarRow>('warState').filter((w) =>
      w.status === 'declared' && (Number(w.aggressorSid) === id || Number(w.defenderSid) === id));
  }

  private siteById(id: number): SettlementSite | undefined {
    return this.sites.find((s) => s.id === id);
  }

  // ── Window ────────────────────────────────────────────────────────────────
  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); this.travelMode = false; this.syncTravelBanner(); this.stopLoops(); return; }
    if (!this.ensureWorld()) return;
    this.ensureOpenSubs();
    if (!this.win) this.build();
    this.win!.setOpen(true);
    this.dirty = true;
    this.startLoops();
    this.renderPanel();
  }

  /** Open in SHUTTLE mode (AtlasTravelModule, from a pad): pick a city or town
   *  to book the 10 SOV hop; hamlets stay visible but unbookable. */
  openTravel(): void {
    if (!this.ensureWorld()) return;
    this.ensureOpenSubs();
    if (!this.win) this.build();
    this.travelMode = true;
    this.win!.setOpen(true);
    this.syncTravelBanner();
    this.dirty = true;
    this.startLoops();
    this.renderPanel();
  }

  private syncTravelBanner(): void {
    if (!this.travelBanner) return;
    this.travelBanner.style.display = this.travelMode ? 'block' : 'none';
  }

  private async bookShuttle(s: SettlementSite): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    try {
      await conn.reducers.shuttleTravel({ targetSiteId: BigInt(s.id) });
    } catch (e) {
      if (this.travelBanner) this.travelBanner.textContent = `⚠ ${String((e as Error).message ?? e)}`;
      return;
    }
    this.travelMode = false;
    this.syncTravelBanner();
    this.win?.setOpen(false);
    this.stopLoops();
    const me = conn.identity ? conn.db.playerState.identity.find(conn.identity) : null;
    // Same world — adopt re-centers AOI + local transform on the arrival pad.
    if (atlas.currentWorldId !== null) {
      bus.emit('atlas:world-adopted', { worldId: atlas.currentWorldId, x: me?.posX ?? s.centerX, z: me?.posZ ?? s.centerZ });
    }
    bus.emit('atlas:status', { text: `Shuttle to ${this.nameOf(s)} — 10 ⓒ` });
  }

  private build(): void {
    this.win = makeWindow({
      id: 'worldmap', title: 'World Atlas', icon: 'map',
      width: 1040, height: 640, minWidth: 760, minHeight: 460, x: 60, y: 48,
      onClose: () => this.stopLoops(),
    });
    const root = document.createElement('div');
    root.style.cssText = 'display:flex;flex-direction:column;height:100%;font:12px/1.45 system-ui;color:#dde';

    // Toolbar
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:8px;align-items:center;padding:6px 8px;border-bottom:1px solid #24344a;flex:0 0 auto';
    const inp = document.createElement('input');
    inp.placeholder = 'find a settlement…';
    inp.style.cssText = 'flex:0 0 200px;background:#0d1626;border:1px solid #2a3f55;border-radius:6px;color:#dde;padding:4px 8px;font:12px system-ui';
    inp.addEventListener('input', () => { this.search = inp.value.trim().toLowerCase(); this.dirty = true; });
    inp.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key !== 'Enter' || !this.search) return;
      const hit = this.sites.find((s) => this.nameOf(s).toLowerCase().includes(this.search));
      if (hit) { this.select(hit); this.center(hit.centerX, hit.centerZ); }
    });
    bar.appendChild(inp);
    const legend = document.createElement('div');
    legend.innerHTML =
      `<span style="color:${CIV_COLOR.a}">●</span> Aurelian Concord&nbsp;&nbsp;` +
      `<span style="color:${CIV_COLOR.b}">●</span> Borean Compact&nbsp;&nbsp;` +
      '<span style="opacity:.6">◉ city · ● town · • hamlet — click a settlement; scroll to zoom · ' +
      '<span style="color:#ffd166">◆</span> right-click sets a waypoint (right-click one to rename/delete)</span>';
    legend.style.cssText = 'flex:1;opacity:.9;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';
    bar.appendChild(legend);
    const meBtn = document.createElement('button');
    meBtn.textContent = '⌖ me';
    meBtn.title = 'Center on your position';
    meBtn.style.cssText = 'background:#1c2a3c;color:#cde;border:1px solid #35506a;border-radius:6px;padding:3px 10px;cursor:pointer';
    meBtn.onclick = () => this.center(atlas.localX, atlas.localZ);
    bar.appendChild(meBtn);
    const fitBtn = document.createElement('button');
    fitBtn.textContent = 'whole world';
    fitBtn.style.cssText = meBtn.style.cssText;
    fitBtn.onclick = () => { this.fitAll(); this.dirty = true; };
    bar.appendChild(fitBtn);
    // Weather overlay toggle (meteo field layers — temperature / weather type /
    // relative humidity). One active at a time; Off hides the layer + legend.
    const wxGroup = document.createElement('div');
    wxGroup.style.cssText = 'display:flex;gap:2px;margin-left:6px';
    const wxDefs: Array<[typeof this.wxMode, string, string]> = [
      ['none', 'wx off', 'Hide the weather layer'],
      ['temp', '🌡 temp', 'Air temperature overlay (°C)'],
      ['weather', '🌦 weather', 'Weather type overlay (rain/snow/fog/cloud)'],
      ['humidity', '💧 humidity', 'Relative humidity overlay (%)'],
      ['civ', '⚑ territory', 'Civilization ownership heatmap (amber Aurelian · blue Borean)'],
      ['war', '⚔ war', 'War layer — light red: declared war · dark red: fighting NOW · red dots: battles (size = fighters; PULSING = live, go there)'],
    ];
    for (const [mode, label, title] of wxDefs) {
      const b = document.createElement('button');
      b.textContent = label;
      b.title = title;
      b.style.cssText = meBtn.style.cssText;
      b.onclick = () => this.setWxMode(mode);
      this.wxButtons.set(mode, b);
      wxGroup.appendChild(b);
    }
    bar.appendChild(wxGroup);
    this.styleWxButtons();
    root.appendChild(bar);

    // Map + side panel
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;flex:1 1 auto;min-height:0';
    this.wrap = document.createElement('div');
    this.wrap.style.cssText = 'flex:1 1 auto;position:relative;overflow:hidden;background:#060a12;min-width:0';
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;cursor:crosshair';
    this.wrap.appendChild(this.canvas);
    this.travelBanner = document.createElement('div');
    this.travelBanner.style.cssText =
      'position:absolute;top:8px;left:50%;transform:translateX(-50%);z-index:4;display:none;' +
      'background:rgba(20,32,52,.95);border:1px solid #5a7ef0;border-radius:8px;padding:5px 14px;' +
      'font:600 12px system-ui;color:#dfe9ff;white-space:nowrap';
    this.travelBanner.textContent = '⇪ SHUTTLE — pick a city or town to travel there (10 ⓒ fare)';
    this.wrap.appendChild(this.travelBanner);
    this.tip = document.createElement('div');
    this.tip.style.cssText =
      'position:absolute;display:none;pointer-events:none;background:rgba(9,13,20,.94);border:1px solid #35506a;' +
      'border-radius:6px;padding:4px 8px;font:11px system-ui;color:#e8f2fc;white-space:nowrap;z-index:3';
    this.wrap.appendChild(this.tip);
    row.appendChild(this.wrap);

    this.panel = document.createElement('div');
    this.panel.style.cssText = 'flex:0 0 340px;border-left:1px solid #24344a;overflow:auto;padding:8px 10px;min-height:0';
    row.appendChild(this.panel);
    root.appendChild(row);
    this.win.body.appendChild(root);

    this.wireCanvas();
    new ResizeObserver(() => { this.resizeCanvas(); }).observe(this.wrap);
    this.resizeCanvas();
  }

  private startLoops(): void {
    if (!this.raf) {
      const loop = (): void => {
        this.raf = this.win?.isOpen() ? requestAnimationFrame(loop) : 0;
        if (this.pulse) this.dirty = true; // a LIVE battle's dot pulses — redraw every frame while one is on the map
        if (this.dirty && this.win?.isOpen()) { this.dirty = false; this.draw(); }
      };
      this.raf = requestAnimationFrame(loop);
    }
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (!this.win?.isOpen()) { this.stopLoops(); return; }
        this.dirty = true; // live rows (wars, econ, versona positions) refresh
        this.renderPanel();
      }, 2500);
    }
  }

  private stopLoops(): void {
    if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
  }

  // ── View math ─────────────────────────────────────────────────────────────
  private cw(): number { return this.canvas?.clientWidth ?? 1; }
  private ch(): number { return this.canvas?.clientHeight ?? 1; }
  private sx(wx: number): number { return (wx - this.view.cx) * this.view.scale + this.cw() / 2; }
  private sz(wz: number): number { return (wz - this.view.cz) * this.view.scale + this.ch() / 2; }
  private wx(px: number): number { return (px - this.cw() / 2) / this.view.scale + this.view.cx; }
  private wz(py: number): number { return (py - this.ch() / 2) / this.view.scale + this.view.cz; }

  private fitAll(): void {
    if (this.sites.length === 0) return;
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
    for (const s of this.sites) {
      x0 = Math.min(x0, s.centerX); x1 = Math.max(x1, s.centerX);
      z0 = Math.min(z0, s.centerZ); z1 = Math.max(z1, s.centerZ);
    }
    this.view.cx = (x0 + x1) / 2;
    this.view.cz = (z0 + z1) / 2;
    this.view.scale = Math.min(this.cw() / Math.max(1, x1 - x0 + 30000), this.ch() / Math.max(1, z1 - z0 + 30000));
  }

  private center(x: number, z: number): void {
    this.view.cx = x; this.view.cz = z;
    if (this.view.scale < 0.004) this.view.scale = 0.012; // arrive at region zoom
    this.dirty = true;
  }

  private resizeCanvas(): void {
    const c = this.canvas;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.max(2, Math.floor(c.clientWidth * dpr));
    c.height = Math.max(2, Math.floor(c.clientHeight * dpr));
    this.dirty = true;
  }

  private wireCanvas(): void {
    const c = this.canvas!;
    let drag: { px: number; py: number; cx: number; cz: number } | null = null;
    c.addEventListener('pointerdown', (e) => {
      drag = { px: e.clientX, py: e.clientY, cx: this.view.cx, cz: this.view.cz };
      c.setPointerCapture(e.pointerId);
    });
    c.addEventListener('pointermove', (e) => {
      const r = c.getBoundingClientRect();
      if (drag) {
        this.view.cx = drag.cx - (e.clientX - drag.px) / this.view.scale;
        this.view.cz = drag.cz - (e.clientY - drag.py) / this.view.scale;
        this.dirty = true;
        return;
      }
      const hit = this.pick(e.clientX - r.left, e.clientY - r.top);
      if (hit !== this.hovered) { this.hovered = hit; this.dirty = true; }
      if (this.tip) {
        if (hit) {
          this.tip.style.display = 'block';
          this.tip.style.left = `${e.clientX - r.left + 14}px`;
          this.tip.style.top = `${e.clientY - r.top + 8}px`;
          // The population count spreads the whole npc_agent table (~6,000
          // rows): only when the hovered site changes, not per pointer event (2026-09-12).
          if (hit !== this.tipFor) {
            this.tipFor = hit;
            const civ = this.civOf(hit);
            const pop = this.tbl<AgentRow>('npcAgent').filter((a) => Number(a.settlementId) === dbSid(hit.id)).length;
            this.tip.innerHTML =
              `<b>${esc(this.nameOf(hit))}</b> <span style="opacity:.7">${hit.tier}</span><br>` +
              `<span style="color:${CIV_COLOR[civ]}">${CIV_TITLE[civ]}</span>` +
              `<span style="opacity:.7"> · ${esc(pretty(hit.biome))}${pop ? ` · ${pop} folk` : ''}</span>`;
          }
        } else { this.tip.style.display = 'none'; this.tipFor = null; }
      }
    });
    const endDrag = (e: PointerEvent): void => {
      if (!drag) return;
      const moved = Math.hypot(e.clientX - drag.px, e.clientY - drag.py);
      drag = null;
      if (moved < 5) {
        const r = c.getBoundingClientRect();
        const hit = this.pick(e.clientX - r.left, e.clientY - r.top);
        if (hit) this.select(hit);
      }
    };
    c.addEventListener('pointerup', endDrag);
    // Right-click: edit the waypoint under the cursor, or set a new one here.
    c.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const conn = atlas.conn;
      if (!conn) return;
      const r = c.getBoundingClientRect();
      const px = e.clientX - r.left, py = e.clientY - r.top;
      let hitW: ReturnType<typeof myWaypoints>[0] | null = null;
      let bestD = 12;
      for (const w of myWaypoints(conn)) {
        const d = Math.hypot(this.sx(w.posX) - px, this.sz(w.posZ) - py);
        if (d < bestD) { bestD = d; hitW = w; }
      }
      if (hitW) promptEditWaypoint(conn, hitW);
      else promptCreateWaypoint(conn, this.wx(px), this.wz(py));
      this.dirty = true;
    });
    c.addEventListener('wheel', (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      const wxAt = this.wx(mx), wzAt = this.wz(my);
      const f = e.deltaY < 0 ? 1.22 : 1 / 1.22;
      this.view.scale = Math.min(6, Math.max(0.00018, this.view.scale * f));
      // Keep the point under the cursor stationary.
      this.view.cx = wxAt - (mx - this.cw() / 2) / this.view.scale;
      this.view.cz = wzAt - (my - this.ch() / 2) / this.view.scale;
      this.dirty = true;
    }, { passive: false });
  }

  private pick(px: number, py: number): SettlementSite | null {
    let best: SettlementSite | null = null;
    let bestD = 13;
    for (const s of this.sites) {
      const d = Math.hypot(this.sx(s.centerX) - px, this.sz(s.centerZ) - py);
      if (!(TIER_R[s.tier] > 0)) continue; // districts are drawn as their capital
      const r = TIER_R[s.tier] + 5;
      if (d < r + 4 && d < bestD) { best = s; bestD = d; }
    }
    return best;
  }

  private select(s: SettlementSite): void {
    if (this.travelMode && (s.tier === 'city' || s.tier === 'town')) {
      void this.bookShuttle(s);
      return;
    }
    this.selected = s;
    this.tab = 'settlement';
    this.ensureSidSubs(BigInt(dbSid(s.id)));
    this.dirty = true;
    this.renderPanel();
  }

  // ── Terrain underlay (async-sliced sampling of the real fields) ───────────
  private kickUnderlay(): void {
    const N = 168;
    const w = this.cw() / this.view.scale, h = this.ch() / this.view.scale;
    const x0 = this.view.cx - w / 2, z0 = this.view.cz - h / 2;
    const key = `${Math.round(x0 / 500)}:${Math.round(z0 / 500)}:${Math.round(Math.log2(this.view.scale) * 6)}`;
    if (this.underKey === key || this.underBuilding === key) return;
    this.underBuilding = key;
    const img = document.createElement('canvas');
    img.width = N; img.height = N;
    const g = img.getContext('2d')!;
    const px = g.createImageData(N, N);
    const seed = this.seed, sea = this.sea;
    let row = 0;
    const slice = (): void => {
      if (this.underBuilding !== key) return; // superseded by a newer view
      const t0 = performance.now();
      while (row < N && performance.now() - t0 < 7) {
        for (let i = 0; i < N; i++) {
          const wxp = x0 + ((i + 0.5) / N) * w, wzp = z0 + ((row + 0.5) / N) * h;
          const ht = terrainHeight(seed, wxp, wzp);
          let r: number, gg: number, b: number;
          if (ht <= sea) {
            const d = Math.min(1, (sea - ht) / 60);
            r = 18 + 12 * (1 - d); gg = 42 + 24 * (1 - d); b = 74 + 34 * (1 - d);
          } else {
            const c = BIOME_COLOR[biomeAt(seed, wxp, wzp)] ?? [90, 90, 90];
            const lum = Math.min(1.45, 0.72 + (ht - sea) / 900);
            // THE WOODS ON THE MAP (flora update 2026-09-07): forest density tints the pixel toward canopy
            // green so stands read at every zoom — the same field that plants the trees.
            const fd = forestDensity(seed, wxp, wzp, sea);
            const fm = fd > 0.45 ? Math.min(1, (fd - 0.45) * 2.2) * 0.62 : 0; // altitude shading
            r = c[0] * lum * (1 - fm) + 30 * fm; gg = c[1] * lum * (1 - fm) + 96 * fm; b = c[2] * lum * (1 - fm) + 42 * fm;
          }
          const o = (row * N + i) * 4;
          px.data[o] = r; px.data[o + 1] = gg; px.data[o + 2] = b; px.data[o + 3] = 255;
        }
        row++;
      }
      if (row < N) { setTimeout(slice, 0); return; }
      g.putImageData(px, 0, 0);
      this.under = { img, x0, z0, x1: x0 + w, z1: z0 + h };
      this.underKey = key;
      this.underBuilding = '';
      this.dirty = true;
    };
    setTimeout(slice, 0);
  }

  // ── Weather overlay (async-sliced meteo sampling, underlay pattern) ────────
  private setWxMode(mode: 'none' | 'temp' | 'weather' | 'humidity' | 'civ' | 'war'): void {
    this.wxMode = mode;
    this.wxKey = '';           // invalidate — rebuild for the new layer
    this.wxBuilding = '';
    this.wxBuildingSpatial = '';
    if (mode === 'none') this.wxOver = null;
    this.styleWxButtons();
    this.dirty = true;
  }

  private styleWxButtons(): void {
    for (const [mode, b] of this.wxButtons) {
      const on = mode === this.wxMode;
      b.style.background = on ? '#2c4a72' : '#1c2a3c';
      b.style.borderColor = on ? '#6b9fe8' : '#35506a';
    }
  }

  private kickWxOverlay(): void {
    if (this.wxMode === 'none' || !this.seed) return;
    const N = 96; // meteoAt is ~4× a terrainHeight sample — keep the grid modest
    const w = this.cw() / this.view.scale, h = this.ch() / this.view.scale;
    const x0 = this.view.cx - w / 2, z0 = this.view.cz - h / 2;
    // The time bucket keys a 60 s refresh — fronts advect at ~11 m/s, so the
    // layer redraws itself as the weather systems move. A bucket flip must
    // NOT supersede an in-flight build (only view/mode changes may): on a
    // slow tab a >60 s build restarted forever and never completed.
    const bucket = Math.floor(Date.now() / 60000);
    const spatial = `${this.wxMode}:${Math.round(x0 / 500)}:${Math.round(z0 / 500)}:${Math.round(Math.log2(this.view.scale) * 6)}`;
    const key = `${spatial}:${bucket}`;
    if (this.wxKey === key) return;
    if (this.wxBuilding !== '' && this.wxBuildingSpatial === spatial) return; // let the in-flight build finish
    this.wxBuilding = key;
    this.wxBuildingSpatial = spatial;
    const img = document.createElement('canvas');
    img.width = N; img.height = N;
    const g = img.getContext('2d')!;
    const px = g.createImageData(N, N);
    const seed = this.seed;
    const mode = this.wxMode;
    const tShared = Date.now() / 1000;
    const tod = currentTod();
    // Conflict layers sample the settlement/war tables, not the weather
    // field — snapshot the inputs once per build (the slicer must not read
    // the live row cache mid-iteration).
    const conflict = mode === 'civ' || mode === 'war' ? this.conflictInputs() : null;
    let row = 0;
    const buildStart = performance.now();
    const slice = (): void => {
      if (this.wxBuilding !== key) return; // superseded by a newer view/mode
      const t0 = performance.now();
      // Adaptive budget: interactive builds finish in ~0.4 s at 12 ms ticks;
      // on a saturated tab (streaming, headless) setTimeout ticks stretch to
      // seconds and 96 rows never finish — after 3 s in flight, take bigger
      // bites so the build completes in a handful of ticks.
      const budget = performance.now() - buildStart > 3000 ? 60 : 12;
      while (row < N && performance.now() - t0 < budget) {
        if (conflict) sampleConflictRow(mode as 'civ' | 'war', conflict.sites, conflict.warSites, conflict.clashes, row, N, x0, z0, w, h, px.data);
        else sampleWxRow(seed, mode as 'temp' | 'weather' | 'humidity', row, N, x0, z0, w, h, tShared, tod, px.data);
        row++;
      }
      if (row < N) { setTimeout(slice, 0); return; }
      g.putImageData(px, 0, 0);
      this.wxOver = { img, x0, z0, x1: x0 + w, z1: z0 + h };
      this.wxKey = key;
      this.wxBuilding = '';
      this.wxBuildingSpatial = '';
      this.dirty = true;
    };
    setTimeout(slice, 0);
  }

  /** Snapshot the conflict-layer inputs (settlements + live wars/clashes). */
  private conflictInputs(): {
    sites: Array<{ x: number; z: number; tier: string; civ: 'a' | 'b' }>;
    warSites: Array<{ x: number; z: number; tier: string }>;
    clashes: Array<{ x: number; z: number }>;
  } {
    const sites = this.sites.map((s) => ({ x: s.centerX, z: s.centerZ, tier: s.tier, civ: this.civOf(s) }));
    const bySid = new Map<number, SettlementSite>();
    for (const s of this.sites) bySid.set(dbSid(s.id), s);
    const warSites: Array<{ x: number; z: number; tier: string }> = [];
    for (const w of this.tbl<WarRow>('warState')) {
      if (w.status !== 'declared') continue;
      for (const sid of [Number(w.aggressorSid), Number(w.defenderSid)]) {
        const s = bySid.get(sid);
        if (s) warSites.push({ x: s.centerX, z: s.centerZ, tier: s.tier });
      }
    }
    const nowMicros = BigInt(Date.now()) * 1000n;
    const clashes: Array<{ x: number; z: number }> = [];
    for (const c of this.tbl<{ x: number; z: number; endsAt: { microsSinceUnixEpoch: bigint } }>('warClash')) {
      if (c.endsAt.microsSinceUnixEpoch > nowMicros) clashes.push({ x: c.x, z: c.z });
    }
    // War rework 2026-09-16: an ENGAGED battle is "fighting NOW" too (the
    // war_clash row is the debug lever's scene; the pitched battle was invisible here).
    for (const b of this.tbl<{ phase: string; targetX: number; targetZ: number }>('battle')) {
      if (b.phase === 'engaged') clashes.push({ x: b.targetX, z: b.targetZ });
    }
    // Allied contributors: a town whose people hold a war duty is at war too
    // (npc_agent is the civ tab's lazy subscription — contributors show once it has loaded).
    const agentSid = new Map<string, number>();
    for (const a of this.tbl<{ versonaId: bigint; settlementId: bigint }>('npcAgent')) agentSid.set(a.versonaId.toString(), Number(a.settlementId));
    if (agentSid.size > 0) {
      const seen = new Set(warSites.map((s) => `${s.x}|${s.z}`));
      for (const w of this.tbl<{ versonaId: bigint }>('warAssignment')) {
        const sid = agentSid.get(w.versonaId.toString());
        const s = sid !== undefined ? bySid.get(sid) : undefined;
        if (s && !seen.has(`${s.centerX}|${s.centerZ}`)) { seen.add(`${s.centerX}|${s.centerZ}`); warSites.push({ x: s.centerX, z: s.centerZ, tier: s.tier }); }
      }
    }
    return { sites, warSites, clashes };
  }

  /** Legend for the active weather layer (bottom-left of the map). */
  private drawWxLegend(g: CanvasRenderingContext2D, H: number): void {
    if (this.wxMode === 'none') return;
    if (this.wxMode === 'civ' || this.wxMode === 'war') {
      const x = 10, y = H - 68, w = 330, h = 36;
      g.fillStyle = 'rgba(9,13,20,.88)';
      g.strokeStyle = '#35506a';
      g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
      g.font = '10px system-ui';
      g.fillStyle = '#cfe0f2';
      if (this.wxMode === 'civ') {
        g.fillText('territorial ownership (influence of settled ground)', x + 8, y + 13);
        g.fillStyle = CIV_COLOR.a; g.fillRect(x + 8, y + 19, 8, 8);
        g.fillStyle = '#a9bed6'; g.fillText(CIV_TITLE.a, x + 19, y + 27);
        g.fillStyle = CIV_COLOR.b; g.fillRect(x + 130, y + 19, 8, 8);
        g.fillStyle = '#a9bed6'; g.fillText(`${CIV_TITLE.b} · pale = contested frontier`, x + 141, y + 27);
      } else {
        g.fillText('war (live)', x + 8, y + 13);
        g.fillStyle = 'rgb(235,110,100)'; g.fillRect(x + 8, y + 19, 8, 8);
        g.fillStyle = '#a9bed6'; g.fillText('declared war', x + 19, y + 27);
        g.fillStyle = 'rgb(150,12,18)'; g.fillRect(x + 100, y + 19, 8, 8);
        g.fillStyle = '#a9bed6'; g.fillText('versonas FIGHTING here right now — visit the dark ground', x + 111, y + 27);
      }
      return;
    }
    const x = 10, y = H - 68, w = this.wxMode === 'weather' ? 330 : 190, h = 36; // sits above the scale bar
    g.fillStyle = 'rgba(9,13,20,.88)';
    g.strokeStyle = '#35506a';
    g.beginPath(); g.roundRect(x, y, w, h, 6); g.fill(); g.stroke();
    g.font = '10px system-ui';
    g.fillStyle = '#cfe0f2';
    if (this.wxMode === 'weather') {
      g.fillText('weather', x + 8, y + 13);
      const classes: WeatherClass[] = ['clear', 'cloudy', 'fog', 'rain', 'storm', 'snow'];
      let cx = x + 8;
      for (const cls of classes) {
        const [r, gg, b] = WEATHER_CLASS_COLOR[cls];
        g.fillStyle = `rgb(${r},${gg},${b})`;
        g.fillRect(cx, y + 19, 8, 8);
        g.fillStyle = '#a9bed6';
        g.fillText(cls, cx + 10, y + 27);
        cx += 12 + g.measureText(cls).width + 6;
      }
      return;
    }
    const isTemp = this.wxMode === 'temp';
    g.fillText(isTemp ? 'air temperature (°C)' : 'relative humidity (%)', x + 8, y + 13);
    const bx = x + 8, bw = w - 16, by = y + 19, bh = 8;
    for (let i = 0; i < bw; i++) {
      const f = i / (bw - 1);
      const [r, gg, b] = isTemp ? tempColor(-25 + f * 65) : humidityColor(f);
      g.fillStyle = `rgb(${r},${gg},${b})`;
      g.fillRect(bx + i, by, 1, bh);
    }
    g.fillStyle = '#a9bed6';
    const labels = isTemp ? ['-25', '0', '20', '40'] : ['0', '50', '100'];
    const fracs = isTemp ? [0, 25 / 65, 45 / 65, 1] : [0, 0.5, 1];
    for (let i = 0; i < labels.length; i++) {
      const lx = bx + fracs[i] * bw - (i === labels.length - 1 ? g.measureText(labels[i]).width : 0);
      g.fillText(labels[i], lx, by + bh + 8);
    }
  }

  // ── Drawing ───────────────────────────────────────────────────────────────
  private draw(): void {
    const c = this.canvas;
    if (!c || !this.win?.isOpen()) return;
    const g = c.getContext('2d');
    if (!g) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = this.cw(), H = this.ch();
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#060a12';
    g.fillRect(0, 0, W, H);

    this.kickUnderlay();
    if (this.under) {
      const u = this.under;
      g.imageSmoothingEnabled = true;
      g.globalAlpha = 0.85;
      g.drawImage(u.img, this.sx(u.x0), this.sz(u.z0), (u.x1 - u.x0) * this.view.scale, (u.z1 - u.z0) * this.view.scale);
      g.globalAlpha = 1;
    }
    // Weather layer above the terrain, below the settlement markers.
    this.kickWxOverlay();
    if (this.wxMode !== 'none' && this.wxOver) {
      const u = this.wxOver;
      g.imageSmoothingEnabled = true;
      g.drawImage(u.img, this.sx(u.x0), this.sz(u.z0), (u.x1 - u.x0) * this.view.scale, (u.z1 - u.z0) * this.view.scale);
    }

    const wars = this.tbl<WarRow>('warState').filter((w) => w.status === 'declared');
    const atWar = new Set<number>();
    for (const w of wars) { atWar.add(Number(w.aggressorSid) - 1); atWar.add(Number(w.defenderSid) - 1); }

    // Versonas of the selected settlement — live positions.
    if (this.selected) {
      g.fillStyle = '#59e6c9';
      // (bug sweep) roster from npc_agent — versona.home_city_id is 0n for
      // every townsperson, so this dot layer was permanently empty.
      const selSid = dbSid(this.selected.id);
      const homeIds = new Set<string>();
      for (const a2 of this.tbl<AgentRow>('npcAgent')) {
        if (Number(a2.settlementId) === selSid) homeIds.add(a2.versonaId.toString());
      }
      for (const v of this.tbl<VersonaRow>('versona')) {
        if (!homeIds.has(v.versonaId.toString())) continue;
        g.fillRect(this.sx(v.curX) - 1.2, this.sz(v.curZ) - 1.2, 2.4, 2.4);
      }
    }

    // The Grand Circuit (phase 5): a thin amber loop, drawn once its samples exist on this thread.
    {
      const seedC = this.seed ?? '';
      const cc = seedC ? peekCircuit(seedC) : null;
      if (cc) {
        g.strokeStyle = 'rgba(255,190,90,.75)';
        g.lineWidth = 1.5;
        g.beginPath();
        const step = Math.max(1, Math.floor(cc.pts.length / 300));
        for (let i = 0; i < cc.pts.length; i += step) { const q = cc.pts[i]; const x = this.sx(q.x), y = this.sz(q.z); if (i === 0) g.moveTo(x, y); else g.lineTo(x, y); }
        g.closePath(); g.stroke();
      }
    }
    const labelTier = this.view.scale > 0.05 ? 'hamlet' : this.view.scale > 0.008 ? 'town' : 'city';
    const show = (t: string): boolean =>
      t === 'city' || t === 'capital' || (t === 'town' && labelTier !== 'city') || labelTier === 'hamlet';
    const q = this.search;
    for (const s of this.sites) {
      const x = this.sx(s.centerX), y = this.sz(s.centerZ);
      if (x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
      const civ = this.civOf(s);
      const r = TIER_R[s.tier];
      if (!(r > 0)) continue;
      const dim = q !== '' && !this.nameOf(s).toLowerCase().includes(q);
      g.globalAlpha = dim ? 0.25 : 1;
      if (atWar.has(s.id)) {
        g.strokeStyle = '#e5534b';
        g.lineWidth = 2;
        g.beginPath(); g.arc(x, y, r + 3.5, 0, Math.PI * 2); g.stroke();
      }
      g.fillStyle = CIV_COLOR[civ];
      g.strokeStyle = '#0a0f18';
      g.lineWidth = 1;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.stroke();
      if (s.tier === 'city' || s.tier === 'capital') { // cities read as ringed seats
        g.strokeStyle = CIV_COLOR[civ];
        g.beginPath(); g.arc(x, y, r + 2.5, 0, Math.PI * 2); g.stroke();
      }
      if (s.tier === 'capital') { // the civ's CAPITAL: a second ring (the megawall) and a four-point star
        g.lineWidth = 1.5;
        g.beginPath(); g.arc(x, y, r + 6, 0, Math.PI * 2); g.stroke();
        g.fillStyle = '#ffffff';
        g.beginPath();
        for (let i = 0; i < 8; i++) { const rr = i % 2 === 0 ? r * 0.78 : r * 0.28; const an = -Math.PI / 2 + (i * Math.PI) / 4; g.lineTo(x + Math.cos(an) * rr, y + Math.sin(an) * rr); }
        g.closePath(); g.fill();
        g.lineWidth = 1;
      }
      if (this.selected?.id === s.id || this.hovered?.id === s.id) {
        g.strokeStyle = '#ffffff';
        g.lineWidth = 1.4;
        g.beginPath(); g.arc(x, y, r + 5.5, 0, Math.PI * 2); g.stroke();
      }
      if (show(s.tier)) {
        g.font = s.tier === 'capital' ? 'bold 13px system-ui' : s.tier === 'city' ? 'bold 12px system-ui' : '10px system-ui';
        g.fillStyle = 'rgba(8,12,20,.75)';
        const name = this.nameOf(s);
        const tw = g.measureText(name).width;
        g.fillRect(x + r + 3, y - 7, tw + 6, 14);
        g.fillStyle = dim ? 'rgba(230,240,255,.4)' : '#e6f0ff';
        g.fillText(name, x + r + 6, y + 4);
      }
      g.globalAlpha = 1;
    }

    this.drawDeposits(g);

    // BATTLES (war rework, 2026-09-16): a red dot per battle, sized by the
    // fighters in it — PULSING while it is live (march / engaged: you can go
    // there), still once it has happened (battle_record, kept a week).
    // Only on the WAR layer (the user's call): the plain map stays clean.
    this.pulse = false;
    if (this.wxMode === 'war') {
      const tNow = performance.now() / 1000;
      const wid = atlas.commonsWorldId;
      const units = new Map<string, number>();
      for (const u of this.tbl<{ battleId: bigint }>('battleUnit')) { const k = u.battleId.toString(); units.set(k, (units.get(k) ?? 0) + 1); }
      const radiusFor = (n: number): number => Math.min(13, 3 + n * 0.55);
      const dot = (x: number, y: number, n: number, live: boolean): void => {
        if (x < -30 || y < -30 || x > W + 30 || y > H + 30) return;
        const base = radiusFor(n);
        const r = live ? base * (1 + 0.28 * Math.sin(tNow * 5)) : base;
        if (live) {
          g.fillStyle = 'rgba(229,83,75,0.22)';
          g.beginPath(); g.arc(x, y, r * 1.9 + 2, 0, Math.PI * 2); g.fill();
        }
        g.fillStyle = live ? '#ff5a4e' : '#b8362f';
        g.strokeStyle = '#1a0806';
        g.lineWidth = 1;
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); g.stroke();
      };
      let anyLive = false, anyFading = false;
      const liveIds = new Set<string>();
      const RECORD_FADE_S = 120; // the last two minutes of a fought battle's dot fade out
      for (const b of this.tbl<{ battleId: bigint; worldId: bigint; phase: string; targetX: number; targetZ: number }>('battle')) {
        if (wid !== null && b.worldId !== wid) continue;
        if (b.phase === 'resolved') continue; // its record draws it
        liveIds.add(b.battleId.toString());
        dot(this.sx(b.targetX), this.sz(b.targetZ), units.get(b.battleId.toString()) ?? 2, true);
        anyLive = true;
      }
      for (const r of this.tbl<{ battleId: bigint; worldId: bigint; x: number; z: number; units: number; resolvedAt: { microsSinceUnixEpoch: bigint } }>('battleRecord')) {
        if (wid !== null && r.worldId !== wid) continue;
        if (liveIds.has(r.battleId.toString())) continue;
        // THE MAP FORGETS IN A QUARTER HOUR (war tweaks 2026-09-17): a fought battle's dot fades over its last two minutes and is gone at RECORD_MAP_S — the record row itself lives a week for the chronicle.
        const age = Date.now() / 1000 - Number(r.resolvedAt.microsSinceUnixEpoch) / 1e6;
        if (age > BATTLE.RECORD_MAP_S) continue;
        const fade = Math.max(0, Math.min(1, (BATTLE.RECORD_MAP_S - age) / RECORD_FADE_S));
        if (fade < 1) { g.globalAlpha = fade; anyFading = true; }
        dot(this.sx(r.x), this.sz(r.z), r.units, false);
        g.globalAlpha = 1;
      }
      this.pulse = anyLive || anyFading;
    }

    // Personal waypoints (own + THIS world only — the planet-aware filter).
    for (const w of myWaypoints(atlas.conn)) {
      const x = this.sx(w.posX), y = this.sz(w.posZ);
      if (x < -80 || y < -80 || x > W + 80 || y > H + 80) continue;
      drawWaypointMarker(g, x, y, w.name);
    }

    // The player.
    if (atlas.currentWorldId !== null) {
      const x = this.sx(atlas.localX), y = this.sz(atlas.localZ);
      if (x > -20 && y > -20 && x < W + 20 && y < H + 20) {
        g.fillStyle = '#7ef0a0';
        g.beginPath();
        g.moveTo(x, y - 6); g.lineTo(x + 5, y + 5); g.lineTo(x - 5, y + 5); g.closePath();
        g.fill();
        g.font = '10px system-ui';
        g.fillText('you', x + 8, y + 4);
      }
    }

    this.drawWxLegend(g, H);

    // Scale bar.
    const meters = 100 / this.view.scale;
    const nice = Math.pow(10, Math.floor(Math.log10(meters)));
    const barM = meters / nice >= 5 ? nice * 5 : meters / nice >= 2 ? nice * 2 : nice;
    const barPx = barM * this.view.scale;
    g.fillStyle = 'rgba(230,240,255,.75)';
    g.fillRect(12, H - 18, barPx, 2);
    g.font = '10px system-ui';
    g.fillText(barM >= 1000 ? `${barM / 1000} km` : `${barM} m`, 12, H - 24);
  }

  // ── Panel ─────────────────────────────────────────────────────────────────
  private renderPanel(): void {
    const p = this.panel;
    if (!p || !this.win?.isOpen()) return;
    const tabs =
      `<div style="display:flex;gap:6px;margin-bottom:8px">` +
      `<button data-tab="settlement" style="${this.tabCss(this.tab === 'settlement')}">Settlement</button>` +
      `<button data-tab="civ" style="${this.tabCss(this.tab === 'civ')}">Civilizations</button></div>`;
    p.innerHTML = tabs + (this.tab === 'civ' ? this.civHtml() : this.settlementHtml());
    for (const b of Array.from(p.querySelectorAll<HTMLButtonElement>('[data-tab]'))) {
      b.addEventListener('click', () => {
        this.tab = b.dataset.tab as 'settlement' | 'civ';
        if (this.tab === 'civ') this.ensureCivTabSubs();
        this.renderPanel();
      });
    }
    for (const b of Array.from(p.querySelectorAll<HTMLButtonElement>('[data-goto]'))) {
      b.addEventListener('click', () => {
        const s = this.siteById(Number(b.dataset.goto));
        if (s) { this.select(s); this.center(s.centerX, s.centerZ); }
      });
    }
  }

  private tabCss(on: boolean): string {
    return `flex:1;padding:4px 0;border-radius:6px;cursor:pointer;border:1px solid ${on ? '#5a7ef0' : '#2a3f55'};` +
      `background:${on ? 'rgba(70,100,220,.25)' : 'transparent'};color:${on ? '#fff' : '#9fb6cf'};font:600 11px system-ui`;
  }

  private h(title: string): string {
    return `<div style="font:700 11px system-ui;letter-spacing:.08em;color:#8fc3ef;margin:10px 0 4px;text-transform:uppercase">${title}</div>`;
  }

  private kv(k: string, v: string): string {
    return `<div style="display:flex;justify-content:space-between;gap:10px;padding:1.5px 0;border-bottom:1px solid #16233a">` +
      `<span style="opacity:.65">${k}</span><span style="text-align:right">${v}</span></div>`;
  }

  private bar(v: number, color: string): string {
    return `<span style="display:inline-block;width:44px;height:6px;background:#16233a;border-radius:3px;vertical-align:1px">` +
      `<span style="display:block;width:${Math.min(100, Math.max(0, v))}%;height:100%;background:${color};border-radius:3px"></span></span>`;
  }

  /** SOCIETY WAVE: the land's deposits, the service posts, the seven town scores, the seat in the Assembly. */
  private societyHtml(sid: number): string {
    const out: string[] = [];
    const nodes = this.tbl<NodeRow>('resourceNode').filter((n) => Number(n.settlementId) === sid);
    const deposits = nodes.filter((n) => n.good.startsWith('dep:'));
    const slots = this.tbl<SlotRow>('jobSlot').filter((j) => Number(j.settlementId) === sid && isServiceKey(j.recipeKey));
    const score = this.tbl<ScoreRow>('settlementScore').find((r) => Number(r.settlementId) === sid);
    const member = this.tbl<MemberRow>('federalMember').find((m) => Number(m.settlementId) === sid);
    const link = this.tbl<LinkRow>('marketLink').find((l) => Number(l.settlementId) === sid);
    const spec = this.tbl<SpecRow>('citySpec').find((c) => Number(c.settlementId) === sid);
    if (deposits.length + slots.length === 0 && !score && !member && !link && !spec) return '';
    out.push(this.h('land & society'));
    if (deposits.length > 0) {
      out.push(this.kv('deposits', deposits.map((d) => `<span style="color:#e8c46a">◆</span> ${esc(pretty(d.good.slice(4)))} <span style="opacity:.5">${d.richness}</span>`).join(' · ')));
    }
    if (spec) out.push(this.kv('known for', esc(pretty(spec.spec))));
    if (link) {
      const city = this.siteById(Number(link.citySid) - 1);
      out.push(this.kv('trades at', city
        ? `<button data-goto="${city.id}" style="background:none;border:none;color:#bcd6f5;text-decoration:underline;cursor:pointer;padding:0">${esc(this.nameOf(city))}</button> <span style="opacity:.5">(a habit, not an overlord)</span>`
        : `#${link.citySid}`));
    }
    if (slots.length > 0) {
      const held = slots.filter((j) => j.holderId !== 0n);
      const kinds = [...new Set(held.map((j) => pretty(j.recipeKey.slice(4))))];
      out.push(this.kv('service posts', `${held.length} of ${slots.length} staffed` + (kinds.length ? ` <span style="opacity:.55">— ${esc(kinds.slice(0, 6).join(', '))}${kinds.length > 6 ? '…' : ''}</span>` : '')));
    }
    if (score) {
      const keys: Array<[keyof ScoreRow, string]> = [['cohesion', '#7bc86c'], ['order', '#6cb2e8'], ['health', '#e57373'], ['learning', '#c9a0ff'], ['piety', '#e8c46a'], ['prosperity', '#e5a34b'], ['readiness', '#c0c8d4']];
      out.push(`<div style="display:grid;grid-template-columns:1fr 1fr;gap:1px 12px;padding:3px 0">` +
        keys.map(([k, col]) => `<span style="display:flex;justify-content:space-between;gap:6px"><span style="opacity:.65">${k}</span><span>${this.bar(Number(score[k]), col)} ${Math.round(Number(score[k]))}</span></span>`).join('') + `</div>`);
    }
    if (member) {
      const votes = TIER_VOTES[member.tier] ?? member.votes;
      const state = member.defiant ? '<span style="color:#f0a8a2">defiant — no votes while its war lasts</span>'
        : member.suspended ? `<span style="color:#e5a34b">suspended — ${member.arrears} coins in arrears</span>` : 'in good standing';
      out.push(this.kv(`Assembly of civ ${esc(member.civ)}`, `${votes} bloc vote${votes === 1 ? '' : 's'} · ${state}`));
    }
    return out.join('');
  }

  /** The selected settlement's deposits, drawn where they lie (a gold diamond each). */
  private drawDeposits(g: CanvasRenderingContext2D): void {
    const s = this.selected;
    if (!s || this.view.scale < 0.02) return; // under ~50 m per pixel the diamonds would pile on the town's own dot
    const sid = s.id + 1;
    for (const n of this.tbl<NodeRow>('resourceNode')) {
      if (Number(n.settlementId) !== sid || !n.good.startsWith('dep:')) continue;
      const x = this.sx(n.x), y = this.sz(n.z);
      g.fillStyle = '#e8c46a'; g.strokeStyle = 'rgba(8,12,20,.85)'; g.lineWidth = 1;
      g.beginPath(); g.moveTo(x, y - 5); g.lineTo(x + 4, y); g.lineTo(x, y + 5); g.lineTo(x - 4, y); g.closePath(); g.fill(); g.stroke();
      if (this.view.scale >= 0.06) {
        g.font = '10px system-ui'; g.fillStyle = '#f3e2b0';
        g.fillText(n.good.slice(4).replace(/_/g, ' '), x + 7, y + 3);
      }
    }
  }

  private settlementHtml(): string {
    const s = this.selected;
    if (!s) return '<div style="opacity:.65;padding:8px 2px">Click a settlement on the map — every stat of the town opens here: its people, offices, laws, markets, wars, and dead.</div>';
    const sid = dbSid(s.id);
    const civ = this.civOf(s);
    const econ = this.econOf(s);
    const agents = this.tbl<AgentRow>('npcAgent').filter((a) => Number(a.settlementId) === sid);
    const agentIdSet = new Set(agents.map((a2) => a2.versonaId.toString())); // (bug sweep) home_city_id is universally 0
    const versonas = this.tbl<VersonaRow>('versona').filter((v) => agentIdSet.has(v.versonaId.toString()));
    const households = this.tbl<HouseholdRow>('household').filter((h2) => Number(h2.settlementId) === sid);
    const memberHouse = new Map<string, string>();
    if (households.length > 0) {
      const byId = new Map(households.map((h2) => [String(h2.householdId), h2.surname]));
      for (const m of this.tbl<HouseholdMemberRow>('householdMember')) {
        const sn = byId.get(String(m.householdId));
        if (sn) memberHouse.set(String(m.versonaId), sn);
      }
    }
    const faiths = new Map(this.tbl<FaithRow>('faithAdherence').map((f) => [String(f.versonaId), f.faithId]));
    const offices = this.tbl<OfficeRow>('settlementOffice').filter((o) => Number(o.settlementId) === sid);
    const graves = this.tbl<GraveRow>('grave').filter((g2) => Number(g2.settlementId) === sid);
    const slots = this.tbl<JobSlotRow>('jobSlot').filter((j) => Number(j.settlementId) === sid);
    const stock = this.tbl<StockRow>('settlementStock').filter((st) => Number(st.settlementId) === sid);
    const rounds = this.tbl<RoundRow>('marketRound').filter((r) => Number(r.settlementId) === sid);
    const clauses = this.tbl<ClauseRow>('charterClause').filter((cl) => Number(cl.settlementId) === sid);
    const norms = this.tbl<NormRow>('settlementNorm').filter((n) => Number(n.settlementId) === sid);
    const residents = this.tbl<ResidencyRow>('residency').filter((r) =>
      Number(r.settlementId) === sid && Number(r.paidUntil.microsSinceUnixEpoch / 1000n) > Date.now());
    const wars = this.warsOf(s.id);
    const rivals = this.tbl<RivalryRow>('settlementRivalry')
      .filter((r) => Number(r.aId) === sid || Number(r.bId) === sid)
      .sort((a, b) => b.grievance - a.grievance);
    const exchange = this.tbl<ExchangeRow>('exchangeOffice').some((x) => Number(x.settlementId) === sid);
    const agentById = new Map(agents.map((a) => [String(a.versonaId), a]));

    const html: string[] = [];
    const dist = Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ);
    html.push(
      `<div style="font:700 16px system-ui;color:#fff">${esc(this.nameOf(s))}</div>` +
      `<div style="margin:1px 0 2px"><span style="color:${CIV_COLOR[civ]}">● ${CIV_TITLE[civ]}</span>` +
      `<span style="opacity:.7"> — ${s.tier} in the ${esc(pretty(s.biome))}</span></div>` +
      `<div style="opacity:.55;font-size:11px">(${Math.round(s.centerX)}, ${Math.round(s.centerZ)}) · ` +
      `${dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${Math.round(dist)} m`} from you</div>`,
    );

    for (const w of wars) {
      const otherId = Number(w.aggressorSid) === sid ? Number(w.defenderSid) : Number(w.aggressorSid);
      const other = this.siteById(otherId - 1);
      const mine = Number(w.aggressorSid) === sid;
      html.push(
        `<div style="margin-top:6px;padding:5px 8px;border:1px solid #6b2b28;border-radius:6px;color:#f0a8a2">` +
        `⚔ AT WAR with <button data-goto="${otherId - 1}" style="background:none;border:none;color:#f0c0bb;text-decoration:underline;cursor:pointer;padding:0">${other ? esc(this.nameOf(other)) : `#${otherId}`}</button>` +
        ` <span style="opacity:.8">(${mine ? 'aggressor' : 'defender'} · ${w.casualties} fallen · exhaustion ${mine ? w.exhaustionA : w.exhaustionB}/100 · ${ago(w.startedAt)})</span></div>`,
      );
    }

    // Governance
    html.push(this.h('governance'));
    if (econ) {
      const mayor = agentById.get(String(econ.mayorId));
      const mayorName = mayor
        ? `the ${esc(mayor.job)}${memberHouse.has(String(mayor.versonaId)) ? ` of the ${esc(memberHouse.get(String(mayor.versonaId))!)} household` : ''}`
        : Number(econ.mayorId) === 0 ? '<span style="opacity:.55">none</span>' : `#${econ.mayorId}`;
      html.push(this.kv('mayor', mayorName));
      html.push(this.kv('sales tax', `${(econ.taxBps / 100).toFixed(1)}%`));
      html.push(this.kv('next election', inH(econ.nextElectionAt)));
      if (econ.projectKey !== '') html.push(this.kv('public works', `${esc(pretty(econ.projectKey))} — ${econ.projectProgress}/${econ.projectTarget}`));
    } else html.push('<div style="opacity:.5">no economy row yet — settle in and the ledger begins</div>');
    for (const o of offices) {
      html.push(this.kv(OFFICE_LABEL[o.office] ?? esc(officeLabel(o.office)), `${esc(o.holderName)} <span style="opacity:.5">(${ago(o.sinceAt)})</span>`));
    }
    if (residents.length > 0) html.push(this.kv('player residents', String(residents.length)));

    html.push(this.societyHtml(sid));

    // Laws
    const activeClauses = clauses.filter((cl) => cl.status !== 'rejected');
    if (activeClauses.length + norms.length > 0) {
      html.push(this.h('laws & customs'));
      for (const cl of activeClauses.slice(0, 8)) {
        html.push(this.kv(`charter · ${esc(pretty(cl.kind))}`, `${cl.value}${cl.status === 'active' ? '' : ` <span style="opacity:.55">(${esc(cl.status)})</span>`}`));
      }
      for (const n of norms.filter((x) => x.active).slice(0, 6)) {
        html.push(this.kv(`custom · ${esc(pretty(n.key))}`,
          `<span title="${esc(n.text)}">${esc(n.text.length > 46 ? `${n.text.slice(0, 44)}…` : n.text)}</span> <span style="opacity:.55">${Math.round(n.adoptionX10 / 10)}% hold to it</span>`));
      }
    }

    // People
    html.push(this.h('people'));
    html.push(this.kv('population', String(Math.max(agents.length, versonas.length)) + (agents.length === 0 ? ' <span style="opacity:.5">(loading…)</span>' : '')));
    if (agents.length > 0) {
      const jobs = new Map<string, number>();
      for (const a of agents) jobs.set(a.job, (jobs.get(a.job) ?? 0) + 1);
      html.push(this.kv('trades', [...jobs.entries()].sort((x, y) => y[1] - x[1]).map(([j, n]) => `${esc(j)} ×${n}`).join(', ')));
      const avg = (f: (a: AgentRow) => number): number => Math.round(agents.reduce((t, a) => t + f(a), 0) / agents.length);
      html.push(this.kv('wellbeing', `mood ${this.bar(avg((a) => a.mood), '#7bc86c')} · hunger ${this.bar(avg((a) => a.hunger), '#e5a34b')}`));
      html.push(this.kv('coin among folk', coin(agents.reduce((t, a) => t + Number(a.wallet), 0))));
    }
    if (households.length > 0) {
      html.push(this.kv('households', `${households.length} — ${households.slice(0, 5).map((h2) => esc(h2.surname)).join(', ')}${households.length > 5 ? '…' : ''}`));
    }
    const faithCounts = new Map<string, number>();
    for (const a of agents) {
      const f = faiths.get(String(a.versonaId));
      if (f) faithCounts.set(f, (faithCounts.get(f) ?? 0) + 1);
    }
    if (faithCounts.size > 0) {
      html.push(this.kv('faiths', [...faithCounts.entries()].map(([f, n]) => `${esc(pretty(f))} ×${n}`).join(', ')));
    }
    if (graves.length > 0) {
      const last = graves[graves.length - 1];
      html.push(this.kv('graves', `${graves.length} — last: ${esc(last.name)} <span style="opacity:.55">(${esc(last.cause)}, ${ago(last.diedAt)})</span>`));
    }

    // Economy
    html.push(this.h('economy'));
    if (econ) {
      html.push(this.kv('treasury', coin(econ.treasury)));
      html.push(this.kv('food', `${econ.stockFood} in store · ${(econ.priceFood / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('ore', `${econ.stockOre} · ${(econ.priceOre / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('tools', `${econ.stockTools} · ${(econ.priceTools / 100).toFixed(2)} ⓒ`));
      html.push(this.kv('luxury', `${econ.stockLuxury} · ${(econ.priceLuxury / 100).toFixed(2)} ⓒ`));
    }
    if (exchange) html.push(this.kv('exchange office', 'SOV ↔ trade crates (U)'));
    if (slots.length > 0) {
      const open = slots.filter((sl) => Number(sl.holderId) === 0);
      html.push(this.kv('labor', `${slots.length - open.length}/${slots.length} posts filled` +
        (open.length ? ` — hiring: ${open.slice(0, 3).map((sl) => `${esc(pretty(sl.fn))} (${sl.wage}ⓒ)`).join(', ')}` : '')));
    }
    if (stock.length > 0) {
      const rows = [...stock].sort((a, b) => b.qty - a.qty);
      html.push(this.kv(`goods in town (${rows.length})`, rows.slice(0, 12).map((st) => `${esc(pretty(st.commodity))} ${Math.round(st.qty)}`).join(' · ')));
    }
    if (rounds.length > 0) {
      const latest = new Map<string, RoundRow>();
      for (const r of rounds) if ((latest.get(r.commodity)?.epoch ?? -1) < r.epoch) latest.set(r.commodity, r);
      const line = [...latest.values()].sort((a, b) => b.volume - a.volume).slice(0, 8)
        .map((r) => `${esc(pretty(r.commodity))} ${r.avgPrice.toFixed(1)}ⓒ×${r.volume}${r.unfilled > 0 ? ` <span style="opacity:.5">(+${r.unfilled} unmet)</span>` : ''}`);
      html.push(this.kv('last market day', line.join(' · ')));
    }

    // Rivals
    if (rivals.length > 0) {
      html.push(this.h('grievances'));
      for (const r of rivals.slice(0, 5)) {
        const otherId = (Number(r.aId) === sid ? Number(r.bId) : Number(r.aId)) - 1;
        const other = this.siteById(otherId);
        html.push(this.kv(
          `<button data-goto="${otherId}" style="background:none;border:none;color:#9fb6cf;text-decoration:underline;cursor:pointer;padding:0">${other ? esc(this.nameOf(other)) : `#${otherId}`}</button>`,
          `${this.bar(r.grievance, r.grievance >= 60 ? '#e5534b' : '#d29922')} ${r.grievance}/100`));
      }
    }

    // The folk themselves
    html.push(this.h(`the folk (${Math.max(agents.length, versonas.length)})`));
    if (agents.length === 0) {
      html.push('<div style="opacity:.5">meeting the townsfolk…</div>');
    }
    const stateOf = new Map(versonas.map((v) => [String(v.versonaId), v.state]));
    for (const a of [...agents].sort((x, y) => Number(y.wallet) - Number(x.wallet)).slice(0, 48)) {
      const key = String(a.versonaId);
      const house = memberHouse.get(key);
      const faith = faiths.get(key);
      html.push(
        `<div style="padding:2.5px 0;border-bottom:1px solid #16233a">` +
        `<span style="color:#cfe3ff">the ${esc(a.job)}</span>` +
        `${house ? ` <span style="opacity:.8">of the ${esc(house)} household</span>` : ` <span style="opacity:.4">#${key}</span>`}` +
        `${faith ? ` <span style="color:#b58cff" title="${esc(pretty(faith))}">◈</span>` : ''}` +
        `<div style="display:flex;gap:8px;font-size:11px;opacity:.85;align-items:center">` +
        `<span>${coin(a.wallet)}</span>` +
        `<span>mood ${this.bar(a.mood, '#7bc86c')}</span>` +
        `<span>hunger ${this.bar(a.hunger, '#e5a34b')}</span>` +
        `${stateOf.has(key) ? `<span style="opacity:.7">${esc(stateOf.get(key)!)}</span>` : ''}` +
        `</div></div>`,
      );
    }
    if (agents.length > 48) html.push(`<div style="opacity:.5;padding:3px 0">…and ${agents.length - 48} more</div>`);
    return html.join('');
  }

  private civHtml(): string {
    this.ensureCivTabSubs();
    const agents = this.tbl<AgentRow>('npcAgent');
    const econs = this.tbl<EconRow>('settlementEcon');
    const graves = this.tbl<GraveRow>('grave');
    const wars = this.tbl<WarRow>('warState').filter((w) => w.status === 'declared');
    const civBySid = new Map<number, Civilization>();
    for (const s of this.sites) civBySid.set(s.id, this.civOf(s));

    const html: string[] = [];
    for (const civ of ['a', 'b'] as Civilization[]) {
      const mySites = this.sites.filter((s) => civBySid.get(s.id) === civ);
      const tiers = { city: 0, town: 0, hamlet: 0, capital: 0, district: 0, circuit: 0 } as Record<string, number>;
      for (const s of mySites) tiers[s.tier]++;
      const sidSet = new Set(mySites.map((s) => dbSid(s.id)));
      const myAgents = agents.filter((a) => sidSet.has(Number(a.settlementId)));
      const myEcons = econs.filter((e) => sidSet.has(Number(e.settlementId)));
      const myGraves = graves.filter((g2) => sidSet.has(Number(g2.settlementId)));
      const myWars = wars.filter((w) => sidSet.has(Number(w.aggressorSid)) || sidSet.has(Number(w.defenderSid)));

      html.push(
        `<div style="margin-top:6px;padding:7px 9px;border:1px solid ${CIV_COLOR[civ]}44;border-radius:8px">` +
        `<div style="font:700 13px system-ui;color:${CIV_COLOR[civ]}">● ${CIV_TITLE[civ]}</div>` +
        `<div style="opacity:.6;font-size:11px;margin-bottom:4px">the ${CIV_FOLK[civ]}</div>`,
      );
      html.push(this.kv('settlements', `${mySites.length} — ${tiers.city} cities · ${tiers.town} towns · ${tiers.hamlet} hamlets`));
      if (myAgents.length > 0) {
        const jobs = new Map<string, number>();
        for (const a of myAgents) jobs.set(a.job, (jobs.get(a.job) ?? 0) + 1);
        const avgMood = Math.round(myAgents.reduce((t, a) => t + a.mood, 0) / myAgents.length);
        const avgHunger = Math.round(myAgents.reduce((t, a) => t + a.hunger, 0) / myAgents.length);
        html.push(this.kv('population', String(myAgents.length)));
        html.push(this.kv('coin among folk', coin(myAgents.reduce((t, a) => t + Number(a.wallet), 0))));
        html.push(this.kv('wellbeing', `mood ${this.bar(avgMood, '#7bc86c')} ${avgMood} · hunger ${this.bar(avgHunger, '#e5a34b')} ${avgHunger}`));
        html.push(this.kv('trades', [...jobs.entries()].sort((x, y) => y[1] - x[1]).slice(0, 6).map(([j, n]) => `${esc(j)} ×${n}`).join(', ')));
      } else {
        html.push('<div style="opacity:.5;padding:2px 0">counting the population…</div>');
      }
      if (myEcons.length > 0) {
        html.push(this.kv('treasuries', coin(myEcons.reduce((t, e) => t + Number(e.treasury), 0))));
        const richest = [...myEcons].sort((x, y) => Number(y.treasury) - Number(x.treasury))[0];
        const rs = this.siteById(Number(richest.settlementId) - 1);
        if (rs) html.push(this.kv('wealthiest', `<button data-goto="${rs.id}" style="background:none;border:none;color:#9fb6cf;text-decoration:underline;cursor:pointer;padding:0">${esc(this.nameOf(rs))}</button> (${coin(richest.treasury)})`));
      }
      if (myGraves.length > 0) html.push(this.kv('the honored dead', String(myGraves.length)));
      html.push(this.kv('wars', myWars.length === 0 ? '<span style="opacity:.6">at peace</span>' : `<span style="color:#e5534b">${myWars.length} active</span>`));
      html.push('</div>');
    }

    if (wars.length > 0) {
      html.push(this.h('active wars'));
      for (const w of wars) {
        const a = this.siteById(Number(w.aggressorSid) - 1), d = this.siteById(Number(w.defenderSid) - 1);
        html.push(this.kv(
          `${a ? esc(this.nameOf(a)) : '?'} ⚔ ${d ? esc(this.nameOf(d)) : '?'}`,
          `${w.casualties} fallen · ${ago(w.startedAt)}`));
      }
    }
    html.push(`<div style="opacity:.45;font-size:11px;margin-top:8px">312-site census derives from the world seed; live rows stream from the server as they change.</div>`);
    return html.join('');
  }
}
